import * as Dialog from '@radix-ui/react-dialog';
import * as Menu from '@radix-ui/react-dropdown-menu';
import { useIsMutating } from '@tanstack/react-query';
import { AlertCircle, Check, CloudOff, Loader2, X } from 'lucide-react';
import { forwardRef, useState, type ButtonHTMLAttributes, type CSSProperties, type ReactNode } from 'react';
import { avatarSrc, isLabelColor, type ColumnColor, type LabelColor, type UserLite } from '@kaba/shared';
import { useConnectionStatus } from '../../lib/realtime';
import { useOutbox } from '../../lib/outbox';
import { mutationCardId, useSaveError } from '../../lib/saveState';
import { dismiss, useToasts } from '../../lib/toast';

export function cx(...c: (string | false | null | undefined)[]) {
  return c.filter(Boolean).join(' ');
}

export const LABEL_BG: Record<LabelColor, string> = {
  green: 'bg-emerald-500',
  yellow: 'bg-amber-400',
  orange: 'bg-orange-500',
  red: 'bg-rose-500',
  purple: 'bg-violet-500',
  blue: 'bg-blue-500',
  sky: 'bg-sky-400',
  lime: 'bg-lime-500',
  pink: 'bg-pink-500',
  gray: 'bg-slate-400',
};

/** 0.49: Spaltenfarben – kräftiger Streifen oben, zarte Fläche (Karten bleiben gut lesbar) */
export const COLUMN_STYLE: Record<LabelColor, { bar: string; tint: string }> = {
  green: { bar: 'border-t-emerald-500', tint: 'bg-emerald-50 dark:bg-emerald-950/40' },
  yellow: { bar: 'border-t-amber-400', tint: 'bg-amber-50 dark:bg-amber-950/40' },
  orange: { bar: 'border-t-orange-500', tint: 'bg-orange-50 dark:bg-orange-950/40' },
  red: { bar: 'border-t-rose-500', tint: 'bg-rose-50 dark:bg-rose-950/40' },
  purple: { bar: 'border-t-violet-500', tint: 'bg-violet-50 dark:bg-violet-950/40' },
  blue: { bar: 'border-t-blue-500', tint: 'bg-blue-50 dark:bg-blue-950/40' },
  sky: { bar: 'border-t-sky-400', tint: 'bg-sky-50 dark:bg-sky-950/40' },
  lime: { bar: 'border-t-lime-500', tint: 'bg-lime-50 dark:bg-lime-950/40' },
  pink: { bar: 'border-t-pink-500', tint: 'bg-pink-50 dark:bg-pink-950/40' },
  gray: { bar: 'border-t-slate-400', tint: 'bg-slate-100 dark:bg-slate-800/60' },
};

/**
 * 0.51: Aussehen einer Spalte für jede Spaltenfarbe – Labelfarben über Klassen, Markenfarbe (folgt dem
 * Erscheinungsbild des Boards) und eigene HEX-Farben über Inline-Stil mit `color-mix` (hell und dunkel).
 * `bar: false` = nur Fläche (z. B. Kopf einer Listengruppe ohne eigenen Streifen).
 */
export function columnLook(color: ColumnColor | null | undefined, opts: { bar?: boolean } = {}): { className: string; style?: CSSProperties } {
  const bar = opts.bar !== false;
  if (!color) return { className: bar ? 'border-t-4 border-t-transparent bg-[var(--column)]' : 'bg-[var(--column)]' };
  if (isLabelColor(color)) return { className: bar ? `border-t-4 ${COLUMN_STYLE[color].bar} ${COLUMN_STYLE[color].tint}` : COLUMN_STYLE[color].tint };
  const c = color === 'brand' ? 'var(--color-accent)' : color;
  return {
    className: bar ? 'border-t-4' : '',
    style: { ...(bar ? { borderTopColor: c } : {}), backgroundColor: `color-mix(in oklab, ${c} 10%, var(--panel))` },
  };
}

/** Name einer Spaltenfarbe für Menüs und Screenreader */
export function columnColorName(color: ColumnColor): string {
  return isLabelColor(color) ? LABEL_NAMES[color] : color === 'brand' ? 'Markenfarbe' : `Eigene Farbe ${color}`;
}

export const LABEL_NAMES: Record<LabelColor, string> = {
  green: 'Grün',
  yellow: 'Gelb',
  orange: 'Orange',
  red: 'Rot',
  purple: 'Lila',
  blue: 'Blau',
  sky: 'Himmelblau',
  lime: 'Limette',
  pink: 'Pink',
  gray: 'Grau',
};

type BtnProps = ButtonHTMLAttributes<HTMLButtonElement> & { variant?: 'primary' | 'ghost' | 'subtle' | 'danger'; size?: 'sm' | 'md' };

export const Button = forwardRef<HTMLButtonElement, BtnProps>(function Button(
  { variant = 'subtle', size = 'md', className, ...rest },
  ref,
) {
  return (
    <button
      ref={ref}
      {...rest}
      className={cx(
        'inline-flex items-center justify-center gap-1.5 rounded-lg font-medium transition-colors disabled:opacity-50 disabled:pointer-events-none select-none',
        size === 'sm' ? 'h-8 px-2.5 text-sm' : 'min-h-11 px-3.5 text-sm sm:min-h-10',
        variant === 'primary' && 'bg-accent text-on-accent hover:brightness-110 active:brightness-95',
        variant === 'subtle' && 'bg-black/5 hover:bg-black/10 dark:bg-white/10 dark:hover:bg-white/15',
        variant === 'ghost' && 'hover:bg-black/5 dark:hover:bg-white/10',
        variant === 'danger' && 'bg-rose-600 text-white hover:bg-rose-700',
        className,
      )}
    />
  );
});

export const IconButton = forwardRef<HTMLButtonElement, ButtonHTMLAttributes<HTMLButtonElement> & { label: string }>(
  function IconButton({ label, className, children, ...rest }, ref) {
    return (
      <button
        ref={ref}
        aria-label={label}
        title={label}
        {...rest}
        className={cx(
          'inline-flex size-11 shrink-0 items-center justify-center rounded-lg text-[var(--ink-soft)] hover:bg-black/5 hover:text-[var(--ink)] sm:size-9 dark:hover:bg-white/10',
          className,
        )}
      >
        {children}
      </button>
    );
  },
);

export function Avatar({ user, size = 24, online }: { user: Pick<UserLite, 'name' | 'image'>; size?: number; online?: boolean }) {
  const [broken, setBroken] = useState(false);
  const initials = user.name
    .split(/\s+/)
    .map((p) => p[0])
    .slice(0, 2)
    .join('')
    .toUpperCase();
  const hue = [...user.name].reduce((a, c) => a + c.charCodeAt(0), 0) % 360;
  // Nur eigene, verkleinerte Profilbilder (nie fremde URLs)
  const src = broken ? null : avatarSrc(user.image, size > 48 ? 256 : 64);
  return (
    <span
      title={online ? `${user.name} (gerade da)` : user.name}
      className={cx(
        'inline-flex shrink-0 items-center justify-center overflow-hidden rounded-full font-semibold text-white ring-2',
        online ? 'ring-emerald-500' : 'ring-[var(--panel)]',
      )}
      style={{ width: size, height: size, fontSize: size * 0.4, background: `hsl(${hue} 45% 45%)` }}
    >
      {src ? <img src={src} alt="" width={size} height={size} className="size-full object-cover" loading="lazy" decoding="async" onError={() => setBroken(true)} /> : initials || '?'}
    </span>
  );
}

/** Dialog: auf dem Handy als Vollbild-Sheet, sonst Panel rechts oder mittig */
export function Sheet({
  open,
  onOpenChange,
  title,
  children,
  side = 'right',
  description,
  header,
  onCloseAutoFocus,
  onEscapeKeyDown,
  focusContainer,
  bare,
  widthClass,
}: {
  open: boolean;
  onOpenChange: (o: boolean) => void;
  title: string;
  children: ReactNode;
  side?: 'right' | 'center' | 'wide';
  description?: string;
  /** Eigener Kopf statt des Titels (der Titel bleibt für Screenreader erhalten) */
  header?: ReactNode;
  onCloseAutoFocus?: (e: Event) => void;
  onEscapeKeyDown?: (e: KeyboardEvent) => void;
  /** Beim Öffnen den Dialog selbst fokussieren statt des ersten Bedienelements (große Dialoge) */
  focusContainer?: boolean;
  /** Ohne Kopfzeile und Scrollbereich – der Inhalt baut beides selbst (Titel bleibt für Screenreader) */
  bare?: boolean;
  /** Breite am Desktop, z. B. `sm:w-[608px]` (nur side="right") */
  widthClass?: string;
}) {
  return (
    <Dialog.Root open={open} onOpenChange={onOpenChange}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-40 bg-black/30 backdrop-blur-[1px]" />
        <Dialog.Content
          aria-describedby={description ? undefined : undefined}
          onCloseAutoFocus={onCloseAutoFocus}
          onEscapeKeyDown={onEscapeKeyDown}
          onOpenAutoFocus={
            focusContainer
              ? (e) => {
                  e.preventDefault();
                  (e.currentTarget as HTMLElement | null)?.focus();
                }
              : undefined
          }
          className={cx(
            'fixed z-50 flex flex-col bg-[var(--panel)] shadow-2xl outline-none',
            'inset-0 sm:inset-auto',
            side === 'right' && cx('sm:top-0 sm:right-0 sm:bottom-0 sm:border-l sm:border-[var(--line)]', widthClass ?? 'sm:w-[560px]'),
            side === 'center' && 'sm:top-[10vh] sm:left-1/2 sm:max-h-[80vh] sm:w-[440px] sm:-translate-x-1/2 sm:rounded-2xl',
            side === 'wide' && 'sm:top-[4vh] sm:left-1/2 sm:h-[92vh] sm:w-[min(1120px,94vw)] sm:-translate-x-1/2 sm:overflow-hidden sm:rounded-2xl',
          )}
        >
          {bare ? (
            <>
              <Dialog.Title className="sr-only">{title}</Dialog.Title>
              {description && <Dialog.Description className="sr-only">{description}</Dialog.Description>}
              {children}
            </>
          ) : (
            <>
              <div className="pt-safe flex items-center gap-2 border-b border-[var(--line)] px-3 py-2">
                <Dialog.Title className={header ? 'sr-only' : 'flex-1 truncate px-1 text-base font-semibold'}>{title}</Dialog.Title>
                {header}
                {description && <Dialog.Description className="sr-only">{description}</Dialog.Description>}
                <Dialog.Close asChild>
                  <IconButton label="Schließen">
                    <X size={20} />
                  </IconButton>
                </Dialog.Close>
              </div>
              <div className="pb-safe min-h-0 flex-1 overflow-y-auto outline-none">{children}</div>
            </>
          )}
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}

export const SheetClose = Dialog.Close;

/** Kurzer Hinweis neben einem Symbol (Maus darüber oder Tastaturfokus); für Screenreader zählt das aria-label */
export function Tip({ text, side = 'right' }: { text: string; side?: 'right' | 'left' | 'bottom' }) {
  return (
    <span
      aria-hidden
      className={cx(
        'pointer-events-none absolute z-40 rounded-md bg-slate-900 px-2 py-1 text-xs font-medium whitespace-nowrap text-white invisible shadow-lg group-hover:visible group-focus-visible:visible dark:bg-slate-100 dark:text-slate-900',
        side === 'right' ? 'top-1/2 left-full ml-2 -translate-y-1/2' : side === 'left' ? 'top-1/2 right-full mr-2 -translate-y-1/2' : 'top-full left-1/2 mt-1.5 -translate-x-1/2',
      )}
    >
      {text}
    </span>
  );
}

export const DropdownMenu = Menu.Root;
export const DropdownTrigger = Menu.Trigger;

/**
 * React reicht Ereignisse aus Portalen an die Eltern im Komponentenbaum weiter – ohne Stopp öffnete
 * z. B. „Verschieben nach …“ im Kartenmenü zusätzlich die Karte selbst. Klick und Tasten gehen darum nicht
 * nach außen. Zeigerereignisse (pointerdown/mousedown/touchstart) dagegen NIE stoppen: Radix erkennt daran am
 * Dokument „Klick daneben“ – gestoppt brauchte das Schließen zwei Klicks und der dritte schloss die Karte (0.52).
 * Ziehgriffe filtern Ereignisse aus Portalen selbst (`ownEvents` in BoardDnd.tsx).
 */
const stop = (e: { stopPropagation: () => void }) => e.stopPropagation();

export function DropdownContent({ children, align = 'end' }: { children: ReactNode; align?: 'start' | 'end' }) {
  return (
    <Menu.Portal>
      <Menu.Content
        onClick={stop}
        onKeyDown={stop}
        align={align}
        sideOffset={4}
        collisionPadding={8}
        className="z-50 min-w-48 rounded-xl border border-[var(--line)] bg-[var(--panel)] p-1 shadow-xl"
      >
        {children}
      </Menu.Content>
    </Menu.Portal>
  );
}

export function DropdownItem({
  children,
  onSelect,
  danger,
  icon,
}: {
  children: ReactNode;
  onSelect: () => void;
  danger?: boolean;
  icon?: ReactNode;
}) {
  return (
    <Menu.Item
      onSelect={onSelect}
      className={cx(
        'flex min-h-11 cursor-pointer items-center gap-2.5 rounded-lg px-2.5 text-sm outline-none select-none sm:min-h-9',
        'data-[highlighted]:bg-black/5 dark:data-[highlighted]:bg-white/10',
        danger && 'text-rose-600',
      )}
    >
      {icon && <span className="text-[var(--ink-soft)]">{icon}</span>}
      {children}
    </Menu.Item>
  );
}

/** Menüeintrag mit Haken; Menü bleibt offen (Mehrfachauswahl) */
export function DropdownCheckItem({
  checked,
  onChange,
  children,
  keepOpen = true,
}: {
  checked: boolean;
  onChange: (checked: boolean) => void;
  children: ReactNode;
  keepOpen?: boolean;
}) {
  return (
    <Menu.CheckboxItem
      checked={checked}
      onCheckedChange={onChange}
      onSelect={(e) => keepOpen && e.preventDefault()}
      className={cx(
        'flex min-h-11 cursor-pointer items-center gap-2.5 rounded-lg px-2.5 text-sm outline-none select-none sm:min-h-9',
        'data-[highlighted]:bg-black/5 dark:data-[highlighted]:bg-white/10',
      )}
    >
      <span
        className={cx(
          'inline-flex size-[18px] shrink-0 items-center justify-center rounded border',
          checked ? 'border-accent bg-accent text-on-accent' : 'border-[var(--line)]',
        )}
        aria-hidden
      >
        {checked && <Check size={13} strokeWidth={3} />}
      </span>
      {children}
    </Menu.CheckboxItem>
  );
}

export function DropdownLabel({ children }: { children: ReactNode }) {
  return <Menu.Label className="px-2.5 pt-2 pb-1 text-xs font-semibold tracking-wide text-[var(--ink-soft)] uppercase">{children}</Menu.Label>;
}

export function DropdownSeparator() {
  return <Menu.Separator className="my-1 h-px bg-[var(--line)]" />;
}

export function Toaster() {
  const toasts = useToasts();
  return (
    <div
      aria-live="polite"
      className="pointer-events-none fixed inset-x-0 bottom-20 z-[60] flex flex-col items-center gap-2 px-4 sm:bottom-6"
    >
      {toasts.map((t) => (
        <div
          key={t.id}
          role={t.kind === 'error' ? 'alert' : 'status'}
          className={cx(
            'pointer-events-auto flex max-w-md items-center gap-3 rounded-xl px-4 py-3 text-sm shadow-lg',
            t.kind === 'error' ? 'bg-rose-600 text-white' : 'bg-slate-900 text-white dark:bg-slate-100 dark:text-slate-900',
          )}
        >
          {t.kind === 'error' && <AlertCircle size={18} className="shrink-0" />}
          <span className="flex-1">{t.text}</span>
          {t.actions?.map((a) => (
            <button
              key={a.label}
              className="min-h-8 shrink-0 font-semibold underline underline-offset-2"
              onClick={() => {
                a.run();
                dismiss(t.id);
              }}
            >
              {a.label}
            </button>
          ))}
        </div>
      ))}
    </div>
  );
}

/**
 * Speicherstatus (Sync): Gespeichert · Speichert … · Offline · Fehler. „Gespeichert“ heißt: vom Server
 * bestätigt. Mit `cardId` zählen nur Änderungen dieser Karte. Am Handy entfällt im Normalzustand das Wort,
 * Offline und Fehler bleiben immer lesbar.
 */
export function SyncStatus({ cardId }: { cardId?: string }) {
  const pending = useIsMutating({
    predicate: (m) => m.options.mutationKey?.[0] === 'save' && (!cardId || mutationCardId(m) === cardId),
  });
  const conn = useConnectionStatus();
  const error = useSaveError(cardId);
  // Offline abgelegte Änderungen (0.44): zählen als „wartet“, nicht als gespeichert
  const queued = useOutbox().filter((o) => !cardId || o.cardId === cardId);
  const refused = queued.filter((o) => o.status !== 'pending').length;
  const [Icon, text, tone, always, detail] = error
    ? [AlertCircle, 'Fehler', 'text-rose-700 dark:text-rose-300', true, error.message]
    : refused > 0
      ? [AlertCircle, 'Nicht übernommen', 'text-rose-700 dark:text-rose-300', true, 'Eine offline gespeicherte Änderung wurde nicht übernommen. Details unten links.']
      : queued.length > 0
        ? [CloudOff, 'Wartet', 'text-amber-700 dark:text-amber-400', true, 'Auf diesem Gerät gespeichert; wird übertragen, sobald eine Verbindung besteht.']
        : pending > 0
      ? [Loader2, 'Speichert …', 'text-[var(--ink-soft)]', false, 'Änderungen werden gespeichert.']
      : conn === 'offline'
        ? [CloudOff, 'Offline', 'text-amber-700 dark:text-amber-400', true, 'Keine Live-Verbindung. Änderungen anderer erscheinen erst nach dem Wiederverbinden.']
        : [Check, 'Gespeichert', 'text-[var(--ink-soft)]', false, 'Alle Änderungen sind auf dem Server gespeichert.'];
  return (
    <span role="status" aria-live="polite" title={detail} className={cx('inline-flex min-h-11 shrink-0 items-center gap-1 px-1 text-xs whitespace-nowrap sm:min-h-9', tone)}>
      <Icon size={15} className={pending > 0 && !error ? 'animate-spin' : undefined} aria-hidden />
      <span className={always ? undefined : 'max-sm:sr-only'}>{text}</span>
      <span className="sr-only">: {detail}</span>
    </span>
  );
}
